import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  InfiniteData,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { Brand } from '@repo/types';

import customInstance, { ErrorType } from '../mutator/custom-instance';
import {
  BrandArray,
  ListBrandsParams,
  ShowBrandByUrlParams,
  ShowBrandParams,
} from './model';

/**
 * @summary List brands
 */
export const listBrands = (params?: ListBrandsParams, signal?: AbortSignal) => {
  return customInstance<BrandArray>({
    url: `/brands`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListBrandsQueryKey = (params?: ListBrandsParams) => {
  return [`/brands`, ...(params ? [params] : [])] as const;
};

export const getListBrandsQueryOptions = <
  TData = Awaited<ReturnType<typeof listBrands>>,
  TError = ErrorType<Error>,
>(
  params?: ListBrandsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listBrands>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBrandsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listBrands>>> = ({
    signal,
  }) => listBrands(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listBrands>>,
    TError,
    TData
  > & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useListBrands<
  TData = Awaited<ReturnType<typeof listBrands>>,
  TError = ErrorType<Error>,
>(
  params?: ListBrandsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listBrands>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListBrandsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List brands with infinite pagination
 */
export const getListBrandsInfiniteQueryOptions = <
  TData = InfiniteData<
    Awaited<ReturnType<typeof listBrands>>,
    ListBrandsParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListBrandsParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listBrands>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listBrands>>,
        QueryKey,
        ListBrandsParams
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBrandsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listBrands>>,
    QueryKey,
    ListBrandsParams
  > = ({ signal, pageParam }) =>
    listBrands({ ...params, ...pageParam }, signal);

  return {
    queryKey,
    queryFn,
    // Hook-level so `lastPageParam` is always defined. React Query v5 requires
    // `initialPageParam`, but the `as` cast below hides its absence from the
    // compiler: without it the first settle calls getNextPageParam with
    // `undefined`, and a caller reading `lastPageParam.page` throws
    // "Cannot read properties of undefined (reading 'page')". Same reasoning as
    // the home-feed hook. Seeding with `params` leaves page one's request
    // byte-identical, since the fetcher already spreads params underneath.
    initialPageParam: params,
    ...queryOptions,
  } as UseInfiniteQueryOptions<
    Awaited<ReturnType<typeof listBrands>>,
    TError,
    TData,
    Awaited<ReturnType<typeof listBrands>>,
    QueryKey,
    ListBrandsParams
  > & { queryKey: DataTag<QueryKey, TData> };
};

export function useListBrandsInfinite<
  TData = InfiniteData<
    Awaited<ReturnType<typeof listBrands>>,
    ListBrandsParams
  >,
  TError = ErrorType<Error>,
>(
  params: ListBrandsParams,
  options?: {
    query?: Partial<
      UseInfiniteQueryOptions<
        Awaited<ReturnType<typeof listBrands>>,
        TError,
        TData,
        Awaited<ReturnType<typeof listBrands>>,
        QueryKey,
        ListBrandsParams
      >
    >;
  },
): UseInfiniteQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
} {
  const queryOptions = getListBrandsInfiniteQueryOptions(params, options);

  const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Info for a specific brand
 */
export const showBrand = (params: ShowBrandParams, signal?: AbortSignal) => {
  return customInstance<Brand>({
    url: `/brands/${params.id}`,
    method: 'GET',
    signal,
  });
};

export const getShowBrandQueryKey = (params: ShowBrandParams) => {
  return [`/brands/${params.id}`] as const;
};

export const getShowBrandQueryOptions = <
  TData = Awaited<ReturnType<typeof showBrand>>,
  TError = ErrorType<Error>,
>(
  params: ShowBrandParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof showBrand>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getShowBrandQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof showBrand>>> = ({
    signal,
  }) => showBrand(params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!params.id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof showBrand>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useShowBrand<
  TData = Awaited<ReturnType<typeof showBrand>>,
  TError = ErrorType<Error>,
>(
  params: ShowBrandParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof showBrand>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getShowBrandQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Info for a specific brand
 */
export const showBrandByUrl = (
  params: ShowBrandByUrlParams,
  signal?: AbortSignal,
) => {
  return customInstance<Brand>({
    url: `/brands/by-url`,
    method: 'GET',
    params,
    signal,
  });
};

export const getShowBrandByUrlQueryKey = (params: ShowBrandByUrlParams) => {
  return [`/brands/by-url`, ...(params ? [params] : [])] as const;
};

export const getShowBrandByUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof showBrandByUrl>>,
  TError = ErrorType<Error>,
>(
  params: ShowBrandByUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof showBrandByUrl>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getShowBrandByUrlQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof showBrandByUrl>>> = ({
    signal,
  }) => showBrandByUrl(params, signal);

  return {
    queryKey,
    queryFn,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof showBrandByUrl>>,
    TError,
    TData
  > & {
    queryKey: DataTag<QueryKey, TData>;
  };
};

export function useShowBrandByUrl<
  TData = Awaited<ReturnType<typeof showBrandByUrl>>,
  TError = ErrorType<Error>,
>(
  params: ShowBrandByUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof showBrandByUrl>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getShowBrandByUrlQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
